import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  type CreateFeedbackFormTemplateDto,
  type CreateFeedbackInviteDto,
  type CreateFeedbackMailTemplateDto,
  FeedbackApi,
  type Feedback,
  type FeedbackFormTemplate,
  type FeedbackInvite,
  type FeedbackInviteDetailDto,
  type FeedbackInvitePreviewSentDto,
  type FeedbackMailTemplate,
  type FeedbackStatsDto,
  type PatchFeedbackEntryDto,
  type FeedbackStatus,
  type FeedbackInviteStatus,
  type SendFeedbackInvitePreviewDto,
  type UpdateFeedbackFormTemplateDto,
  type UpdateFeedbackInviteDto,
  type UpdateFeedbackMailTemplateDto,
} from '@up2stay/up2promo-api-client';

import { useEntitlements } from '@/core/hooks/use-entitlements';

const STALE_TIME = 30_000;

/**
 * Whether the workspace plan includes Feedback. Without it every feedback
 * endpoint answers 403 (and the 403 raises an upgrade toast on top of the
 * teaser card), so the queries below stay idle (DEV-549).
 */
export function useFeedbackFeatureEnabled(): boolean {
  return useEntitlements().isFeatureEnabled('feature.feedback');
}

export function feedbackQueryKeys(workspaceId: string) {
  return {
    all: ['feedback', workspaceId] as const,
    entries: (filters: Record<string, unknown>) =>
      ['feedback', workspaceId, 'entries', filters] as const,
    entry: (entryId: string) =>
      ['feedback', workspaceId, 'entry', entryId] as const,
    stats: ['feedback', workspaceId, 'stats'] as const,
    invites: (filters: Record<string, unknown>) =>
      ['feedback', workspaceId, 'invites', filters] as const,
    invite: (inviteId: string) =>
      ['feedback', workspaceId, 'invite', inviteId] as const,
    contacts: (search?: string) =>
      ['feedback', workspaceId, 'contacts', search ?? ''] as const,
    formTemplates: ['feedback', workspaceId, 'form-templates'] as const,
    formTemplate: (templateId: string) =>
      ['feedback', workspaceId, 'form-template', templateId] as const,
    mailTemplates: ['feedback', workspaceId, 'mail-templates'] as const,
    mailTemplate: (templateId: string) =>
      ['feedback', workspaceId, 'mail-template', templateId] as const,
  };
}

export function useFeedbackStats(
  workspaceId: string | undefined,
  options?: Pick<UseQueryOptions<FeedbackStatsDto | undefined>, 'enabled'>,
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).stats
      : ['feedback', 'stats', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.getStats({
        path: { workspaceId: workspaceId as string },
      });
      return res.data as FeedbackStatsDto | undefined;
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackEntries(
  workspaceId: string | undefined,
  query: {
    status?: FeedbackStatus;
    locationId?: string;
    limit?: number;
    skip?: number;
  } = {},
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).entries(query)
      : ['feedback', 'entries', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.listEntries({
        path: { workspaceId: workspaceId as string },
        query: {
          status: query.status,
          locationId: query.locationId,
          limit: query.limit,
          skip: query.skip,
        },
      });
      return (res.data ?? []) as Feedback[];
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackEntry(
  workspaceId: string | undefined,
  entryId: string | undefined,
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey:
      workspaceId && entryId
        ? feedbackQueryKeys(workspaceId).entry(entryId)
        : ['feedback', 'entry', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.getEntry({
        path: {
          workspaceId: workspaceId as string,
          entryId: entryId as string,
        },
      });
      return res.data as Feedback | undefined;
    },
    enabled: feedbackEnabled && !!workspaceId && !!entryId,
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackInvites(
  workspaceId: string | undefined,
  query: { status?: FeedbackInviteStatus; limit?: number } = {},
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).invites(query)
      : ['feedback', 'invites', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.listInvites({
        path: { workspaceId: workspaceId as string },
        query: {
          status: query.status,
          limit: query.limit,
        },
      });
      return (res.data ?? []) as FeedbackInvite[];
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackInvite(
  workspaceId: string | undefined,
  inviteId: string | undefined,
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey:
      workspaceId && inviteId
        ? feedbackQueryKeys(workspaceId).invite(inviteId)
        : ['feedback', 'invite', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.getInvite({
        path: {
          workspaceId: workspaceId as string,
          inviteId: inviteId as string,
        },
      });
      return res.data as FeedbackInviteDetailDto | undefined;
    },
    enabled:
      feedbackEnabled &&
      !!workspaceId &&
      !!inviteId &&
      (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackContacts(
  workspaceId: string | undefined,
  search?: string,
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).contacts(search)
      : ['feedback', 'contacts', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.listContacts({
        path: { workspaceId: workspaceId as string },
        query: search ? { search, limit: 100 } : { limit: 100 },
      });
      return res.data ?? [];
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function usePatchFeedbackEntry(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      entryId: string;
      body: PatchFeedbackEntryDto;
    }) => {
      const res = await FeedbackApi.patchEntry({
        path: {
          workspaceId: workspaceId as string,
          entryId: params.entryId,
        },
        body: params.body,
      });
      return res.data as Feedback;
    },
    onSuccess: async (_data, vars) => {
      if (!workspaceId) return;
      const keys = feedbackQueryKeys(workspaceId);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: keys.all }),
        queryClient.invalidateQueries({
          queryKey: keys.entry(vars.entryId),
        }),
      ]);
    },
  });
}

export function useCreateFeedbackInvite(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFeedbackInviteDto) => {
      const res = await FeedbackApi.createInvite({
        path: { workspaceId: workspaceId as string },
        body,
      });
      return res.data as FeedbackInviteDetailDto;
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).all,
      });
    },
  });
}

export function useUpdateFeedbackInvite(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      inviteId: string;
      body: UpdateFeedbackInviteDto;
    }) => {
      const res = await FeedbackApi.updateInvite({
        path: {
          workspaceId: workspaceId as string,
          inviteId: params.inviteId,
        },
        body: params.body,
      });
      return res.data as FeedbackInviteDetailDto;
    },
    onSuccess: async (_data, vars) => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).invite(vars.inviteId),
      });
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).all,
      });
    },
  });
}

export function useSendFeedbackInvite(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await FeedbackApi.sendInvite({
        path: { workspaceId: workspaceId as string, inviteId },
      });
      return res.data as FeedbackInviteDetailDto;
    },
    onSuccess: async (data) => {
      if (!workspaceId || !data?.id) return;
      const keys = feedbackQueryKeys(workspaceId);
      queryClient.setQueryData(keys.invite(data.id), data);
      await queryClient.invalidateQueries({
        queryKey: keys.all,
        refetchType: 'active',
      });
    },
  });
}

export function useSendFeedbackInvitePreview(workspaceId: string | undefined) {
  return useMutation({
    mutationFn: async (body: SendFeedbackInvitePreviewDto) => {
      const res = await FeedbackApi.sendPreviewMail({
        path: { workspaceId: workspaceId as string },
        body,
      });
      return res.data as FeedbackInvitePreviewSentDto;
    },
  });
}

export function useCancelFeedbackInvite(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await FeedbackApi.cancelInvite({
        path: { workspaceId: workspaceId as string, inviteId },
      });
      return res.data as FeedbackInviteDetailDto;
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).all,
      });
    },
  });
}

export function useResendFeedbackInviteRecipient(
  workspaceId: string | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { inviteId: string; recipientId: string }) => {
      const res = await FeedbackApi.resendInviteRecipient({
        path: {
          workspaceId: workspaceId as string,
          inviteId: params.inviteId,
          recipientId: params.recipientId,
        },
      });
      return res.data as FeedbackInviteDetailDto;
    },
    onSuccess: async (data, vars) => {
      if (!workspaceId) return;
      const keys = feedbackQueryKeys(workspaceId);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: keys.invite(vars.inviteId),
        }),
        queryClient.invalidateQueries({ queryKey: keys.all }),
      ]);
      if (data) {
        queryClient.setQueryData(keys.invite(vars.inviteId), data);
      }
    },
  });
}

export function useFeedbackFormTemplates(
  workspaceId: string | undefined,
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).formTemplates
      : ['feedback', 'form-templates', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.listFormTemplates({
        path: { workspaceId: workspaceId as string },
      });
      return (res.data ?? []) as FeedbackFormTemplate[];
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackFormTemplate(
  workspaceId: string | undefined,
  templateId: string | undefined,
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey:
      workspaceId && templateId
        ? feedbackQueryKeys(workspaceId).formTemplate(templateId)
        : ['feedback', 'form-template', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.getFormTemplate({
        path: {
          workspaceId: workspaceId as string,
          templateId: templateId as string,
        },
      });
      return res.data as FeedbackFormTemplate | undefined;
    },
    enabled: feedbackEnabled && !!workspaceId && !!templateId,
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useCreateFeedbackFormTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFeedbackFormTemplateDto) => {
      const res = await FeedbackApi.createFormTemplate({
        path: { workspaceId: workspaceId as string },
        body,
      });
      return res.data as FeedbackFormTemplate;
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).formTemplates,
      });
    },
  });
}

export function useUpdateFeedbackFormTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      templateId: string;
      body: UpdateFeedbackFormTemplateDto;
    }) => {
      const res = await FeedbackApi.updateFormTemplate({
        path: {
          workspaceId: workspaceId as string,
          templateId: params.templateId,
        },
        body: params.body,
      });
      return res.data as FeedbackFormTemplate;
    },
    onSuccess: async (_data, vars) => {
      if (!workspaceId) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: feedbackQueryKeys(workspaceId).formTemplates,
        }),
        queryClient.invalidateQueries({
          queryKey: feedbackQueryKeys(workspaceId).formTemplate(
            vars.templateId,
          ),
        }),
      ]);
    },
  });
}

export function useDeleteFeedbackFormTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await FeedbackApi.deleteFormTemplate({
        path: { workspaceId: workspaceId as string, templateId },
      });
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).formTemplates,
      });
    },
  });
}

export function useFeedbackMailTemplates(
  workspaceId: string | undefined,
  options?: { enabled?: boolean },
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey: workspaceId
      ? feedbackQueryKeys(workspaceId).mailTemplates
      : ['feedback', 'mail-templates', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.listMailTemplates({
        path: { workspaceId: workspaceId as string },
      });
      return (res.data ?? []) as FeedbackMailTemplate[];
    },
    enabled: feedbackEnabled && !!workspaceId && (options?.enabled ?? true),
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useFeedbackMailTemplate(
  workspaceId: string | undefined,
  templateId: string | undefined,
) {
  const feedbackEnabled = useFeedbackFeatureEnabled();
  return useQuery({
    queryKey:
      workspaceId && templateId
        ? feedbackQueryKeys(workspaceId).mailTemplate(templateId)
        : ['feedback', 'mail-template', 'none'],
    queryFn: async () => {
      const res = await FeedbackApi.getMailTemplate({
        path: {
          workspaceId: workspaceId as string,
          templateId: templateId as string,
        },
      });
      return res.data as FeedbackMailTemplate | undefined;
    },
    enabled: feedbackEnabled && !!workspaceId && !!templateId,
    staleTime: STALE_TIME,
    retry: false,
  });
}

export function useCreateFeedbackMailTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFeedbackMailTemplateDto) => {
      const res = await FeedbackApi.createMailTemplate({
        path: { workspaceId: workspaceId as string },
        body,
      });
      return res.data as FeedbackMailTemplate;
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).mailTemplates,
      });
    },
  });
}

export function useUpdateFeedbackMailTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      templateId: string;
      body: UpdateFeedbackMailTemplateDto;
    }) => {
      const res = await FeedbackApi.updateMailTemplate({
        path: {
          workspaceId: workspaceId as string,
          templateId: params.templateId,
        },
        body: params.body,
      });
      return res.data as FeedbackMailTemplate;
    },
    onSuccess: async (_data, vars) => {
      if (!workspaceId) return;
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: feedbackQueryKeys(workspaceId).mailTemplates,
        }),
        queryClient.invalidateQueries({
          queryKey: feedbackQueryKeys(workspaceId).mailTemplate(
            vars.templateId,
          ),
        }),
      ]);
    },
  });
}

export function useDeleteFeedbackMailTemplate(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await FeedbackApi.deleteMailTemplate({
        path: { workspaceId: workspaceId as string, templateId },
      });
    },
    onSuccess: async () => {
      if (!workspaceId) return;
      await queryClient.invalidateQueries({
        queryKey: feedbackQueryKeys(workspaceId).mailTemplates,
      });
    },
  });
}
